import { Loader2 } from 'lucide-react';
import { useCallback, useMemo, useState, type ReactNode } from 'react';

import {
  AdminBulkActionRow,
  AdminItemCard,
  InlineError,
  Pill,
  SkeletonCardGrid,
  Switch,
} from '@/design-system';
import { cn } from '@/shared/cn';
import { BUTTON_GHOST_ADMIN, BUTTON_PRIMARY } from '@/shared/ui-class';
import type { BedrockModelHealthEntry } from '@/versions/v1/shared/admin-seed';
import {
  formatContextWindow,
  type AgentKitId,
  type AppModel,
  type AllowlistConfigValue,
} from './AiModelsScene.transform';

function allowlistStateFromIds(
  allowedIds: string[] | null,
  catalog: readonly AppModel[],
) {
  if (allowedIds === null) {
    const allIds = new Set(catalog.map((m) => m.id));
    return {
      mode: 'all' as const,
      selected: allIds,
      initialMode: 'all' as const,
      initialSelected: new Set(allIds),
    };
  }

  const ids = new Set(allowedIds);
  return {
    mode: 'list' as const,
    selected: ids,
    initialMode: 'list' as const,
    initialSelected: new Set(ids),
  };
}

export type ModelAllowlistPanelBaseProps = {
  kit: AgentKitId;
  catalog: readonly AppModel[];
  configSlug: string;
  panelDescription: string;
  disabledHint?: string;
  denyAllWarning: string;
  children?: ReactNode;
  buildSaveValue?: (base: AllowlistConfigValue) => Record<string, unknown>;
  onConfigLoaded?: (value: Record<string, unknown>) => void;
  extraDirty?: boolean;
  modelHealth?: ReadonlyMap<string, BedrockModelHealthEntry>;
  initialAllowedIds: string[] | null;
  onSave: (value: Record<string, unknown>) => void;
};

export function ModelAllowlistPanelBase({
  kit: _kit,
  catalog,
  panelDescription,
  disabledHint,
  denyAllWarning,
  children,
  buildSaveValue,
  onConfigLoaded,
  extraDirty = false,
  modelHealth,
  initialAllowedIds,
  onSave,
}: ModelAllowlistPanelBaseProps) {
  const [initialAllowlist] = useState(() =>
    allowlistStateFromIds(initialAllowedIds, catalog),
  );
  const [mode, setMode] = useState<'all' | 'list'>(initialAllowlist.mode);
  const [selected, setSelected] = useState<Set<string>>(initialAllowlist.selected);
  const [initialMode, setInitialMode] = useState<'all' | 'list'>(
    initialAllowlist.initialMode,
  );
  const [initialSelected, setInitialSelected] = useState<Set<string>>(
    initialAllowlist.initialSelected,
  );
  const [loading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [confirmDenyAll, setConfirmDenyAll] = useState(false);
  const [kitDisabledHint] = useState(() => catalog.length === 0);

  const applyAllowlistValue = useCallback(
    (allowedIds: string[] | null) => {
      const next = allowlistStateFromIds(allowedIds, catalog);
      setMode(next.mode);
      setSelected(next.selected);
      setInitialMode(next.initialMode);
      setInitialSelected(next.initialSelected);
    },
    [catalog],
  );

  const loadConfig = useCallback(() => {
    setError(null);
    applyAllowlistValue(initialAllowedIds);
    onConfigLoaded?.({ allowedIds: initialAllowedIds });
  }, [applyAllowlistValue, initialAllowedIds, onConfigLoaded]);

  void loadConfig;

  const dirty = useMemo(() => {
    if (extraDirty) return true;
    if (mode !== initialMode) return true;
    if (selected.size !== initialSelected.size) return true;
    return [...selected].some((id) => !initialSelected.has(id));
  }, [extraDirty, mode, initialMode, selected, initialSelected]);

  const countLine =
    mode === 'all'
      ? `All ${catalog.length} enabled — new catalog models are allowed automatically`
      : `${selected.size} of ${catalog.length} enabled`;

  const toggleEntry = (id: string, checked: boolean) => {
    setMode('list');
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
    setConfirmDenyAll(false);
  };

  const selectAll = () => {
    setMode('list');
    setSelected(new Set(catalog.map((m) => m.id)));
    setConfirmDenyAll(false);
  };

  const selectNone = () => {
    setMode('list');
    setSelected(new Set());
    setConfirmDenyAll(true);
  };

  const resetToDefault = () => {
    setMode('all');
    setSelected(new Set(catalog.map((m) => m.id)));
    setConfirmDenyAll(false);
  };

  const onSaveClick = () => {
    setSaving(true);
    setSaveError(null);
    const base: AllowlistConfigValue = {
      allowedIds: mode === 'all' ? null : [...selected].sort(),
      defaultId: null,
    };
    const value = buildSaveValue ? buildSaveValue(base) : base;
    try {
      onSave(value);
      applyAllowlistValue(base.allowedIds);
      setConfirmDenyAll(false);
    } catch {
      setSaveError('Failed to save config');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted">{panelDescription}</p>

      {disabledHint && kitDisabledHint ? (
        <p className="text-sm text-muted">{disabledHint}</p>
      ) : null}

      {error ? <InlineError>{error}</InlineError> : null}

      {loading ? (
        <SkeletonCardGrid count={3} cols={2} />
      ) : (
        <>
          <AdminBulkActionRow left={countLine}>
            <button type="button" onClick={selectAll} className={BUTTON_GHOST_ADMIN}>
              Select all
            </button>
            <button type="button" onClick={selectNone} className={BUTTON_GHOST_ADMIN}>
              Select none
            </button>
            <button type="button" onClick={resetToDefault} className={BUTTON_GHOST_ADMIN}>
              Reset to default (allow all)
            </button>
          </AdminBulkActionRow>

          <div className="grid gap-3 sm:grid-cols-2">
            {catalog.map((m) => {
              const checked = mode === 'all' || selected.has(m.id);
              const healthEntry = modelHealth?.get(m.id);
              return (
                <AdminItemCard
                  key={m.id}
                  control={
                    <Switch
                      checked={checked}
                      onCheckedChange={(next) => toggleEntry(m.id, next)}
                      aria-label={`Allow ${m.label}`}
                    />
                  }
                  title={m.label}
                  description={m.description}
                  badges={
                    <>
                      <Pill>{formatContextWindow(m.contextWindowTokens)}</Pill>
                      {healthEntry ? (
                        healthEntry.status === 'ok' ? (
                          <Pill tone="success">Available</Pill>
                        ) : (
                          <Pill tone="danger" title={healthEntry.errorMessage ?? undefined}>
                            Unavailable
                          </Pill>
                        )
                      ) : null}
                    </>
                  }
                />
              );
            })}
          </div>

          {children}

          {confirmDenyAll && mode === 'list' && selected.size === 0 ? (
            <p className="text-sm text-muted">{denyAllWarning}</p>
          ) : null}

          {saveError ? <InlineError>{saveError}</InlineError> : null}

          <div className="flex justify-end">
            <button
              type="button"
              disabled={!dirty || saving}
              onClick={onSaveClick}
              className={cn(BUTTON_PRIMARY)}
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Save
            </button>
          </div>
        </>
      )}
    </div>
  );
}
